/* The home screen: what happened, and where to go next.
 *
 * Direction 1a from `Mobile Home Directions.dc.html` with the efficiency
 * calendar from 1b, which is what `Mill Mobile App.dc.html` settled on: a
 * KPI board, the month as a heat calendar, then the six dashboards as tiles
 * with a sparkline each.
 *
 * Owner and Manager are a view preference, not a permission — the signed-in
 * role still decides what the app will show anyone. The switch reorders the
 * tiles and swaps the four KPIs: an owner opens on money (sales, cotton
 * bought, the month's tonnage, stock cover), a manager on the floor
 * (yesterday's efficiency and output, heads present, units per kilo).
 */

import type { MobileSummary } from '../../lib/api_mobile'
import {
  BpButton, CR, Calendar, IN, KG, KpiGrid, P1, RS, SG, Spark, TN, kpi,
  type CalDay, type MKpi,
} from '../parts'
import type { Role, ScreenId } from '../model'

/** A dashboard tile. Keyed by the screen it opens — every screen but home,
 *  which is where the tiles are. */
export type TileId = Exclude<ScreenId, 'home'>

export interface Tile {
  id: TileId
  name: string
  value: string
  sub: string
  bad?: boolean
  series: number[]
}

/** The order each role reads the mill in. */
const ORDER: Record<Role, TileId[]> = {
  owner: ['sales', 'cotton', 'purchases', 'production', 'stock', 'workforce'],
  manager: ['production', 'workforce', 'stock', 'cotton', 'purchases', 'sales'],
}

export function homeTiles(d: MobileSummary): Record<TileId, Tile> {
  const days = d.prod.days
  const last = days[days.length - 1]
  const st = d.stock.days
  const sl = st[st.length - 1]
  const sales = d.sales.months[d.sales.months.length - 1]
  const purch = d.purch.months.find((m) => m.mk === d.purch.mk)
  const rate = d.rates[d.rates.length - 1]
  const hr = d.hr
  const attendance = hr.empty ? null
    : hr.totals.presentDays / (hr.register * hr.dayCount) * 100

  return {
    production: {
      id: 'production', name: 'Production',
      value: last ? KG(last.a) : '–',
      sub: last ? `${P1(last.e)} eff · ${last.d} ${d.label.slice(0, 3)}` : 'no days on file',
      bad: !!last && last.e < d.bench,
      series: days.map((x) => x.e),
    },
    stock: {
      id: 'stock', name: 'Stock report',
      value: sl ? TN(sl.yarn) : '–',
      sub: d.stock.cover == null ? 'yarn closing' : `yarn closing · ${d.stock.cover} days`,
      series: st.map((x) => x.yarn),
    },
    cotton: {
      id: 'cotton', name: 'Cotton rates',
      value: rate ? RS(rate.last) : '–',
      sub: rate ? `${rate.k} · CAI` : 'no rates on file',
      series: rate ? rate.trend : [],
    },
    purchases: {
      id: 'purchases', name: 'Cotton purchases',
      value: purch ? `${IN(purch.bales)} bales` : '–',
      sub: purch ? `${d.purch.label.slice(0, 3)} · ${CR(purch.v)}` : 'no bills on file',
      series: d.purch.months.map((m) => m.bales),
    },
    sales: {
      id: 'sales', name: 'Yarn & waste sales',
      value: sales ? CR(sales.v) : '–',
      sub: sales ? `${d.sales.label.slice(0, 3)} · ${TN(sales.kg)} yarn` : 'no register on file',
      series: d.sales.months.map((m) => m.v),
    },
    workforce: {
      id: 'workforce', name: 'Workforce',
      value: attendance == null ? '–' : P1(attendance),
      sub: hr.empty ? 'no register on file'
        : `attendance · gap ${SG(hr.avgPresent - hr.req, IN)}`,
      bad: attendance != null && attendance < 80,
      series: hr.days.map((x) => x.p),
    },
  }
}

function kpis(d: MobileSummary, role: Role): MKpi[] {
  const days = d.prod.days
  const last = days[days.length - 1]
  const st = d.stock.days
  const sl = st[st.length - 1]
  const sales = d.sales.months[d.sales.months.length - 1]
  const purch = d.purch.months.find((m) => m.mk === d.purch.mk)
  const hr = d.hr
  const sumA = days.reduce((s, x) => s + x.a, 0)
  const sumT = days.reduce((s, x) => s + x.t, 0)
  const mEff = sumT ? sumA / sumT * 100 : 0
  const short = d.label.slice(0, 3)

  if (role === 'owner') {
    return [
      kpi(`Yarn sales · ${d.sales.label.slice(0, 3)}`, sales ? CR(sales.v) : '–',
        sales ? `${TN(sales.kg)} · ${sales.n} invoices` : 'register not loaded'),
      kpi(`Cotton bought · ${d.purch.label.slice(0, 3)}`, purch ? CR(purch.v) : '–',
        purch ? `${IN(purch.bales)} bales · ${RS(purch.candy)}/candy` : 'register not loaded'),
      kpi(`Production · ${short}`, TN(sumA), `${P1(mEff)} of target`, mEff < d.bench),
      kpi('Yarn stock', sl ? TN(sl.yarn) : '–',
        d.stock.cover == null ? 'nothing dispatched yet'
          : `${d.stock.cover} days of dispatch`),
    ]
  }
  return [
    kpi(`Efficiency · ${last ? `${last.d} ${short}` : short}`, last ? P1(last.e) : '–',
      `bench ${d.bench}% · month ${P1(mEff)}`, !!last && last.e < d.bench),
    kpi(`Production · ${last ? `${last.d} ${short}` : short}`, last ? KG(last.a) : '–',
      last ? `of ${KG(last.t)} target` : 'no days on file'),
    kpi('Present / day', hr.empty ? '–' : IN(hr.avgPresent),
      hr.empty ? 'register not loaded'
        : `${hr.req} required · gap ${SG(hr.avgPresent - hr.req, IN)}`,
      !hr.empty && hr.avgPresent < hr.req),
    kpi(`Power · ${last ? `${last.d} ${short}` : short}`,
      last?.ukg == null ? '–' : `${last.ukg} UKG`,
      last?.eb == null ? 'no meter reading'
        : `EB ${IN(last.eb)} · solar ${IN(last.solar)} units`),
  ]
}

export function Home({ data, role, dark, onOpen, onPickDay }: {
  data: MobileSummary
  role: Role
  dark: boolean
  onOpen: (id: ScreenId) => void
  /** Tapping a day on the calendar opens the production screen on it. */
  onPickDay: (index: number) => void
}) {
  const tiles = homeTiles(data)
  const days = data.prod.days
  const last = days[days.length - 1]
  const cal: CalDay[] = days.map((x) => ({ d: x.d, v: x.e, dow: x.dow }))

  return (
    <>
      <div className="m-eyebrow" style={{ marginTop: 22 }}>Mohans Sri Chand Tex</div>
      <h1 style={{ fontSize: 38, lineHeight: 1, margin: '6px 0 4px' }}>Today</h1>
      <div className="m-sub">
        {last ? `${last.dow} ${last.d} ${data.label} · ` : ''}latest close, Unit 1 + 2
      </div>

      <KpiGrid items={kpis(data, role)} />

      <div className="m-head" style={{ margin: '30px 0 10px' }}>
        <h2 className="m-h2">Efficiency · {data.label.split(' ')[0]}</h2>
        <span className="m-aside">tap a day</span>
      </div>
      <Calendar days={cal} bench={data.bench} dark={dark} onPick={onPickDay} />

      <div className="m-head" style={{ margin: '30px 0 12px' }}>
        <h2 className="m-h2">Dashboards</h2>
        <span className="m-aside">{data.prod.machines} frames</span>
      </div>
      <div className="m-grid-2">
        {ORDER[role].map((id) => {
          const t = tiles[id]
          return (
            <BpButton key={id} className="m-tile" onClick={() => onOpen(id)}>
              <span style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 600,
                  lineHeight: 1.15,
                }}>{t.name}</span>
                <span style={{ fontSize: 14, color: 'var(--color-neutral-500)' }}>›</span>
              </span>
              <span className="m-num" style={{
                fontFamily: 'var(--font-heading)', fontSize: 24, lineHeight: 1,
                marginTop: 6, color: t.bad ? 'var(--c-bad)' : 'var(--color-text)',
              }}>{t.value}</span>
              <span style={{
                fontSize: 11, lineHeight: 1.35, color: 'var(--color-neutral-700)',
              }}>{t.sub}</span>
              <Spark series={t.series} />
            </BpButton>
          )
        })}
      </div>
    </>
  )
}
